import BusinessReview from "@/components/VenueDetailBasic/BusinessReview";
import { useDarkMode } from "@/hooks/useDarkMode";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shadcn/components/ui/dropdown-menu";

import { useRouter } from "@tanstack/react-router";
import { createFileRoute } from "@tanstack/react-router";
import { useI18N } from "common/hooks";
import { useSyncReviews } from "common/hooks/business/details/useSyncReviews";
import { useEffect, useState } from "react";
import NoReviews from "@/assets/images/noreviews.svg";
import Loader from "@/components/common/Loader";
import { StandardButton } from "@/components/common/StandardButton";
import { Helmet } from "react-helmet";

type VenueReviewsRouteSearchParams = {
  sortBy: "popularity" | "latest";
};

export const Route = createFileRoute("/venuereviews/$venueId")({
  component: VenueReviews,
  validateSearch: (
    search: Record<string, unknown>,
  ): VenueReviewsRouteSearchParams => {
    return {
      sortBy: search.source as VenueReviewsRouteSearchParams["sortBy"],
    };
  },
});

function VenueReviews() {
  const { venueId } = Route.useParams();
  const { sortBy } = Route.useSearch();
  const isDark = useDarkMode();
  const i18n = useI18N();

  const [currentSort, setCurrentSort] = useState<"popularity" | "latest">(
    sortBy || "latest",
  );

  const { data, error, isLoading, loadReviews } = useSyncReviews({
    venueId,
    sortBy: currentSort,
  });

  const router = useRouter();
  const onBack = () => router.history.back();

  useEffect(() => {
    if (error) {
      console.error(error);
    }
  }, [error]);

  useEffect(() => {
    loadReviews();
  }, [currentSort]);

  return (
    <div className="px-4">
      <Helmet>
        <title>All Reviews | Sociability</title>
      </Helmet>
      <main id="main" className="max-w-[1160px] mx-auto">
        <div className="flex items-center bg-bg-brand-secondary-default mb-2 mt-1 justify-between">
          <StandardButton
            onClick={() => onBack()}
            variant="back"
            className="flex items-center gap-2 px-1 py-3 bg-bg-brand-secondary-default rounded-3xl text-center body-l-semibold"
            imageSrc={
              isDark ? "/images/ArrowLeft_dark.svg" : "/images/ArrowLeft.svg"
            }
            imageAlt=""
            imageContainerClassName="w-6 h-6 relative"
          >
            {i18n.get("back")}
          </StandardButton>
        </div>
        <section className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 mb-4 items-center">
          <h1 className="text-fg-neutral-strong title-s-semibold">
            {i18n.get("allReviews")}
          </h1>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="h-10 px-4 bg-bg-brand-secondary-default rounded-3xl border border-blue-950 flex items-center gap-1 w-full max-w-[120px] md:max-w-[100px] justify-center focus-blue"
                aria-label="Sort reviews"
              >
                <div className="w-6 h-6 relative">
                  <img
                    src={
                      isDark
                        ? "/images/ListBullets_dark.svg"
                        : "/images/ListBullets.svg"
                    }
                    alt=""
                    aria-hidden="true"
                  />
                </div>
                <span className="body-l-semibold text-fg-neutral-strong">
                  Sort
                </span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel
                tabIndex={0}
                aria-label={i18n.get("sortBy")}
                className="focus-blue"
              >
                {i18n.get("sortBy")}
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuRadioGroup
                value={currentSort}
                onValueChange={(value) => {
                  setCurrentSort(value as any);
                }}
              >
                <DropdownMenuRadioItem value="latest">
                  Date added (Newest)
                </DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="popularity">
                  Popularity
                </DropdownMenuRadioItem>
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </section>
        <ul className="grid grid-col-1 md:grid-cols-3 gap-4" role="list">
          {data?.map((review) => (
            <li key={review.id}>
              <BusinessReview review={review} onFlag={() => {}} />
            </li>
          ))}
        </ul>
        {data?.length === 0 && !isLoading && (
          <img
            src={NoReviews}
            alt="No reviews have been submitted yet. Be the first to review this venue!"
            className="max-w-[70%] mx-auto mt-20"
          />
        )}
        {isLoading && <Loader />}
      </main>
    </div>
  );
}

export default VenueReviews;
